import type { TreeInput } from '@goaltree/backend/convex/nodes'
import { cn } from '@goaltree/ui/lib/utils'
import { Check, Flame } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useHabitCap } from '@/components/habit-cap-hint'
import { periodLabel } from '@/lib/schedule'
import { formatMinutes } from '@/lib/task-time'
import { openHabitUpsell } from '@/lib/usage-limit'

type PlanHabit = NonNullable<TreeInput['habits']>[number]

const clockLabel = (hhmm: string, locale: string) => {
  const [h, m] = hhmm.split(':').map(Number)
  return new Date(2000, 0, 1, h, m).toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

// The plan's suggested routines, created as habits when it's saved; untick to skip one.
export function PlanHabits({
  habits,
  off,
  onToggle,
}: {
  habits: PlanHabit[]
  off: Set<number>
  onToggle: (index: number) => void
}) {
  const { t, i18n } = useTranslation()
  const { used, limit } = useHabitCap()
  if (!habits.length) return null
  // Ticked habits past the Free allowance aren't created on save.
  const room = limit === null ? Infinity : Math.max(0, limit - used)
  const proOnly = new Set(
    habits
      .map((_, i) => i)
      .filter((i) => !off.has(i))
      .slice(room),
  )
  const onCount = habits.length - off.size
  return (
    <section className="mt-6 flex flex-col gap-2">
      <div>
        <h3 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          {t('planner.habitsTitle')}
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('planner.habitsHint')}</p>
      </div>
      {habits.map((h, i) => {
        const on = !off.has(i)
        const pro = proOnly.has(i)
        return (
          <button
            key={`${h.title}-${i}`}
            type="button"
            role="checkbox"
            aria-checked={on}
            onClick={() => onToggle(i)}
            className={cn(
              'flex w-full cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors',
              on
                ? 'border-primary/50 bg-primary/10'
                : 'border-border/60 bg-card/40 hover:border-border',
            )}
          >
            <Flame
              className={cn('size-4 shrink-0', on ? 'text-orange-500' : 'text-muted-foreground/50')}
              strokeWidth={1.8}
            />
            <span className="min-w-0 flex-1">
              <span className={cn('block text-sm font-medium', !on && 'text-muted-foreground')}>
                {h.title}
              </span>
              <span className="block text-xs text-muted-foreground">
                {periodLabel(t, i18n.language, h.cadence, h.days)}
                {h.minutes ? ` · ${formatMinutes(h.minutes)}` : ''}
                {h.time ? ` · ${clockLabel(h.time, i18n.language)}` : ''}
              </span>
            </span>
            {pro && (
              <span className="shrink-0 rounded-full bg-primary/12 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-text">
                {t('habitLimit.proBadge')}
              </span>
            )}
            <span
              className={cn(
                'flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                on ? 'border-primary bg-primary' : 'border-border/60',
              )}
            >
              {on && <Check className="size-3 text-primary-foreground" strokeWidth={3} />}
            </span>
          </button>
        )
      })}
      {proOnly.size > 0 && (
        <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
          <span className="min-w-0 flex-1">{t('habitLimit.planNudge', { count: onCount })}</span>
          <button
            type="button"
            onClick={openHabitUpsell}
            className="shrink-0 cursor-pointer font-semibold text-primary-text hover:underline"
          >
            {t('habitLimit.upgrade')}
          </button>
        </div>
      )}
    </section>
  )
}
